import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

import { isBrowser } from '../is-browser';

/**
 * Browser-session-storage-backed store tracking which nudges have already been shown.
 * Entries expire after 7 days; capped at 500 to prevent unbounded growth.
 */

const STORAGE_KEY = 'rovo-button-nudges-suppression';

// Bounded growth constraints.
const MAX_ENTRIES = 500;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Map of suppression key → expiresAt (epoch ms). */
export type SuppressionMap = Map<string, number>;

/** In-memory cache. Prevents JSON.parse on every render. */
let memoryCache: SuppressionMap | null = null;

/**
 * Drop expired entries and, if still over the cap, drop the oldest by expiry.
 * Returns a new map (pure).
 */
const pruneEntries = (map: SuppressionMap, now: number = Date.now()): SuppressionMap => {
	// 1. Drop expired entries.
	const fresh: SuppressionMap = new Map();
	for (const [key, expiresAt] of map) {
		if (expiresAt > now) {
			fresh.set(key, expiresAt);
		}
	}
	// 2. If still over cap, drop oldest entries by expiry.
	if (fresh.size <= MAX_ENTRIES) {
		return fresh;
	}
	const sorted = Array.from(fresh.entries()).sort((a, b) => a[1] - b[1]);
	return new Map(sorted.slice(sorted.length - MAX_ENTRIES));
};

/** Best-effort write of a suppression map to sessionStorage. Memory cache is always source of truth. */
const writeToStorage = (map: SuppressionMap): void => {
	try {
		const value = JSON.stringify(Array.from(map.entries()));
		AtlBrowserStorageSession.setItem(STORAGE_KEY, value);
	} catch {
		// Best-effort. Memory cache is always source of truth.
	}
};

/** Parse the persisted JSON. Returns an empty Map on miss/corrupt. */
const parseFromStorage = (): SuppressionMap => {
	try {
		const raw = AtlBrowserStorageSession.getItem(STORAGE_KEY);
		if (!raw) {
			return new Map();
		}
		const arr: unknown = JSON.parse(raw);
		if (
			Array.isArray(arr) &&
			arr.every(
				(e): e is [string, number] =>
					Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number',
			)
		) {
			return new Map(arr);
		}
	} catch {
		// Corrupt JSON — start fresh.
	}
	return new Map();
};

/**
 * Read the suppression map.
 * - SSR-safe: returns empty Map on the server.
 * - Memo-cached: only parses sessionStorage on first call per session.
 * - Read-only: never writes back to sessionStorage. Writes happen only in writeSuppression.
 */
export const readSuppressionMap = (): SuppressionMap => {
	if (!isBrowser()) {
		return new Map();
	}
	if (memoryCache) {
		// Defensive copy so callers cannot mutate the cache.
		return new Map(memoryCache);
	}
	const parsed = parseFromStorage();
	memoryCache = parsed;
	return new Map(parsed);
};

/**
 * Records a suppression entry for the given key. Prunes expired/excess entries on every write.
 * Note: sessionStorage is per-tab — a user with multiple tabs open may see the same nudge once per tab.
 */
export const writeSuppression = (suppressionKey: string): void => {
	if (!isBrowser()) {
		return;
	}
	const current = readSuppressionMap();
	// Guard against silently refreshing TTL on an already-suppressed key.
	if (current.has(suppressionKey)) {
		return;
	}
	const next = new Map(current);
	next.set(suppressionKey, Date.now() + MAX_AGE_MS);
	const pruned = pruneEntries(next);
	memoryCache = pruned;
	writeToStorage(pruned);
};

/** Returns true if the suppression key has an active, non-expired entry. */
export const isKeySuppressed = (suppressionKey: string): boolean => {
	const expiresAt = readSuppressionMap().get(suppressionKey);
	return expiresAt !== undefined && expiresAt > Date.now();
};

/** Remove a single suppression entry. Used by the debug API. */
export const clearSuppression = (suppressionKey: string): void => {
	if (!isBrowser()) {
		return;
	}
	const next = new Map(readSuppressionMap());
	next.delete(suppressionKey);
	memoryCache = next;
	writeToStorage(next);
};

/** Remove all suppression entries. Used by the debug API. */
export const clearAllSuppression = (): void => {
	if (!isBrowser()) {
		return;
	}
	memoryCache = new Map();
	try {
		AtlBrowserStorageSession.removeItem(STORAGE_KEY);
	} catch {
		// Best-effort.
	}
};
